import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Inbox, Loader2, Search, SlidersHorizontal, Sparkles, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { CommunityNeedCard } from "@/components/social/CommunityNeedCard";
import { MyNeedCard } from "@/components/social/MyNeedCard";
import { OtterAssistResponderModal } from "@/components/social/OtterAssistResponderModal";
import { OtterAssistPreferences } from "@/components/social/OtterAssistPreferences";
import { OtterAssistEditDialog } from "@/components/social/OtterAssistEditDialog";
import { OtterAssistEmailIntent } from "@/components/social/OtterAssistEmailIntent";
import { AssistanceHub } from "@/components/social/AssistanceHub";

// Otter Assist V1 Phase 6 + Phase 7 — Engagement → Otter Assist (OAD-072 / OAD-087 … OAD-091).
//
// Phase 7 adds recipient search, need-derived facet filters, the approved sorting options, the full
// sender management dashboard and the Preferences entry point. Nothing here shows a Match Quality
// score, band or rank, and nothing shows a members-reached, contacted or wave number.

const NEED_SORTS = [
  { value: "urgency", label: "Urgency & deadline" },
  { value: "relevance", label: "Most relevant" },
  { value: "received_desc", label: "Newest first" },
  { value: "received_asc", label: "Oldest first" },
  { value: "identity", label: "Name A–Z" },
];
const MY_SORTS = [
  { value: "status", label: "Status" },
  { value: "urgency", label: "Urgency & deadline" },
  { value: "created", label: "Date created" },
  { value: "offered", label: "Number offered" },
];
const MY_FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "help_received", label: "Help received" },
  { value: "closed", label: "Closed" },
  { value: "expired", label: "Expired" },
  { value: "archived", label: "Archived" },
];
const SCROLL_KEY = "oa-scroll-position";

/** A debounced search box shared by both tabs. */
function SearchBox({ value, onChange, placeholder, testId }) {
  const [text, setText] = useState(value || "");
  useEffect(() => { setText(value || ""); }, [value]);
  useEffect(() => {
    const timer = setTimeout(() => { if (text !== (value || "")) onChange(text); }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <div className="relative min-w-[190px] flex-1">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder}
        aria-label={placeholder} className="h-8 pl-8 text-[12px]" data-testid={testId} />
      {text && (
        <button type="button" onClick={() => { setText(""); onChange(""); }}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          data-testid={`${testId}-clear`}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function FacetSelect({ label, options, value, onChange, testId }) {
  if (!options || options.length === 0) return null;
  return (
    <div className="min-w-[150px]">
      <Label className="text-[10px] font-[600] uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className="mt-1 h-8 text-[12px]" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid={`${testId}-all`}>Any {label.toLowerCase()}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`${testId}-${o.value}`}>
              {o.label} ({o.count})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CommunityNeeds() {
  const [view, setView] = useState("active");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("urgency");
  const [filters, setFilters] = useState({ industry: "", region: "", work_setting: "", identity_type: "" });
  // OAD-110 — a convenience filter inside the DELIVERED inbox. Assistance Offered remains the
  // cross-source view that also holds public-discovery offers.
  const [offeredOnly, setOfferedOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const params = useMemo(() => ({
    view, sort, limit: 25,
    q: q || undefined,
    industry: filters.industry || undefined,
    region: filters.region || undefined,
    work_setting: filters.work_setting || undefined,
    identity_type: filters.identity_type || undefined,
    offered_only: offeredOnly || undefined,
  }), [view, sort, q, filters, offeredOnly]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["oa-community-needs", params],
    queryFn: async () => (await api.get("/otter-assist/community-needs", { params })).data,
  });
  const items = data?.items || [];
  const facets = data?.facets || {};
  const activeFilters = Object.values(filters).filter(Boolean).length + (offeredOnly ? 1 : 0);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <div data-testid="otter-assist-community-needs">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant={view === "active" ? "default" : "outline"}
          className="h-8 text-[11px]" onClick={() => setView("active")}
          data-testid="community-needs-filter-active">Current</Button>
        <Button type="button" size="sm" variant={view === "archived" ? "default" : "outline"}
          className="h-8 text-[11px]" onClick={() => setView("archived")}
          data-testid="community-needs-filter-archived">Archived</Button>
        <SearchBox value={q} onChange={setQ} placeholder="Search needs and people"
          testId="community-needs-search" />
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-8 w-[165px] text-[12px]" aria-label="Sort Community Needs"
            data-testid="community-needs-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {NEED_SORTS.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`community-needs-sort-${s.value}`}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="button" size="sm" variant={showFilters ? "default" : "outline"}
          className="h-8 text-[11px]" onClick={() => setShowFilters((v) => !v)}
          aria-expanded={showFilters} data-testid="community-needs-filters-toggle">
          <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
          Filters{activeFilters ? ` (${activeFilters})` : ""}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-secondary/30 p-3"
          data-testid="community-needs-filters">
          <FacetSelect label="Industry" options={facets.industries} value={filters.industry}
            onChange={(v) => setFilter("industry", v)} testId="community-needs-facet-industry" />
          <FacetSelect label="Region" options={facets.regions} value={filters.region}
            onChange={(v) => setFilter("region", v)} testId="community-needs-facet-region" />
          <FacetSelect label="Work setting" options={facets.work_settings}
            value={filters.work_setting} onChange={(v) => setFilter("work_setting", v)}
            testId="community-needs-facet-work-setting" />
          <FacetSelect label="Posted as" options={facets.identity_types}
            value={filters.identity_type} onChange={(v) => setFilter("identity_type", v)}
            testId="community-needs-facet-identity" />
          <div>
            <Label className="text-[11px] text-muted-foreground">My response</Label>
            <div className="mt-1">
              <Button type="button" size="sm" variant={offeredOnly ? "default" : "outline"}
                className="h-8 text-[11px]" onClick={() => setOfferedOnly((v) => !v)}
                aria-pressed={offeredOnly} data-testid="community-needs-filter-offered">
                Offered to Help
              </Button>
            </div>
          </div>
          {activeFilters > 0 && (
            <Button type="button" size="sm" variant="ghost" className="h-8 text-[11px]"
              onClick={() => {
                setFilters({ industry: "", region: "", work_setting: "", identity_type: "" });
                setOfferedOnly(false);
              }}
              data-testid="community-needs-filters-clear">Clear filters</Button>
          )}
          {!facets.industries?.length && !facets.regions?.length && !facets.work_settings?.length && (
            <p className="text-[11px] text-muted-foreground" data-testid="community-needs-no-facets">
              The needs you have received don't carry industry, region or work-setting details yet.
            </p>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : isError ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center"
          data-testid="community-needs-error">
          <p className="text-sm text-foreground">
            Something went wrong with Otter Assist. Please try again.
          </p>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center"
          data-testid="community-needs-empty">
          <Inbox className="mx-auto h-7 w-7 text-muted-foreground" />
          <p className="mt-3 text-sm font-[600] text-foreground">
            {q || activeFilters
              ? "Nothing matches that search"
              : view === "archived" ? "No archived needs." : "No new Community Needs right now."}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {q || activeFilters
              ? "Try a different word, or clear your filters."
              : view === "archived"
                ? "Needs you archive or set aside will appear here."
                : "When someone posts a need that fits you, it will land here."}
          </p>
        </div>
      ) : (
        <>
          <p className="mb-2 text-[11px] text-muted-foreground" data-testid="community-needs-count">
            {data.total === 1 ? "1 need" : `${data.total} needs`}
          </p>
          <div className="space-y-3">
            {items.map((need) => (
              <CommunityNeedCard key={need.delivery_id} need={need} view={view} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MyNeeds() {
  const [openAssist, setOpenAssist] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("status");

  const params = useMemo(() => ({ status, sort, limit: 25, q: q || undefined }),
    [status, sort, q]);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["oa-my-needs", params],
    queryFn: async () => (await api.get("/otter-assist/my-needs", { params })).data,
  });
  const items = data?.items || [];
  const allowance = data?.allowance || {};

  return (
    <div data-testid="otter-assist-my-needs">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-secondary/30 px-3 py-2.5"
        data-testid="my-needs-allowance">
        <div className="text-[12px] text-foreground">
          <span className="font-[640]" data-testid="my-needs-allowance-remaining">
            {allowance.remaining ?? 0}
          </span>
          <span className="text-muted-foreground">
            {" "}of {allowance.total ?? 0} Assists left
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground" data-testid="my-needs-allowance-reset">
          {allowance.cycle_end
            ? `Resets ${new Date(allowance.cycle_end).toLocaleDateString()}`
            : "Resets with your billing cycle"}
          {allowance.open_limit ? ` · ${allowance.open_count ?? 0}/${allowance.open_limit} active` : ""}
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchBox value={q} onChange={setQ} placeholder="Search your needs"
          testId="my-needs-search" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-8 w-[150px] text-[12px]" aria-label="Filter my needs"
            data-testid="my-needs-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MY_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value} data-testid={`my-needs-status-${f.value}`}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-8 w-[165px] text-[12px]" aria-label="Sort my needs"
            data-testid="my-needs-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MY_SORTS.map((s) => (
              <SelectItem key={s.value} value={s.value} data-testid={`my-needs-sort-${s.value}`}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : isError ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center"
          data-testid="my-needs-error">
          <p className="text-sm text-foreground">
            Something went wrong with Otter Assist. Please try again.
          </p>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-14 text-center"
          data-testid="my-needs-empty">
          <Sparkles className="mx-auto h-7 w-7 text-muted-foreground" />
          <p className="mt-3 text-sm font-[600] text-foreground">
            {q || status !== "all"
              ? "Nothing matches that view"
              : "You don't have any Otter Assist requests yet."}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Post a request on Chatter with Otter Assist and it will show up here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((need) => (
            <MyNeedCard key={need.assist_id} need={need} onOpenResponders={setOpenAssist}
              onEdit={setEditing} />
          ))}
        </div>
      )}

      <OtterAssistResponderModal assistId={openAssist} open={!!openAssist}
        onOpenChange={(v) => { if (!v) setOpenAssist(null); }} />
      {editing && (
        <OtterAssistEditDialog assistId={editing.assist_id}
          identityType={editing.identity?.profile_type || "personal"}
          onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

export default function OtterAssist() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefsOpen, setPrefsOpen] = useState(false);
  const tabParam = searchParams.get("tab");
  const tab = tabParam === "mine" ? "mine" : tabParam === "assistance" ? "assistance" : "community";
  const restored = useRef(false);

  // Phase 8 (OAD-100) — an emailed link only navigates here. The intent opens a confirmation and
  // is consumed immediately, so it is never re-applied on a refresh or shared URL.
  const [intent, setIntent] = useState(null);
  useEffect(() => {
    const need = searchParams.get("need");
    const wanted = searchParams.get("intent");
    if (!need || !wanted) return;
    setIntent({ deliveryId: need, intent: wanted });
    const next = new URLSearchParams(searchParams);
    next.delete("intent");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll / return preservation — coming back from View Post lands where the member left off.
  // Throttled through requestAnimationFrame so the listener never writes on every scroll event.
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const stored = Number(sessionStorage.getItem(SCROLL_KEY) || 0);
    if (stored > 0) setTimeout(() => window.scrollTo({ top: stored }), 120);
  }, []);
  useEffect(() => {
    let queued = false;
    const remember = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
      });
    };
    window.addEventListener("scroll", remember, { passive: true });
    return () => {
      sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
      window.removeEventListener("scroll", remember);
    };
  }, []);

  const setTab = useCallback((value) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="mx-auto max-w-3xl px-4 pt-6 pb-10 sm:px-6" data-testid="otter-assist-page">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-[680] tracking-tight text-foreground sm:text-3xl">
            <Sparkles className="h-6 w-6 text-teal-700" /> Otter Assist
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Needs the community has shared with you, the needs you've posted, and the help you've
            offered or received.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" className="h-8 text-[11px]"
          onClick={() => setPrefsOpen(true)} data-testid="otter-assist-preferences-button">
          <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" /> Preferences
        </Button>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-5" data-testid="otter-assist-tabs">
          <TabsTrigger value="community" data-testid="otter-assist-tab-community">Community Needs</TabsTrigger>
          <TabsTrigger value="mine" data-testid="otter-assist-tab-mine">My Needs</TabsTrigger>
          <TabsTrigger value="assistance" data-testid="otter-assist-tab-assistance">Assistance</TabsTrigger>
        </TabsList>
        <TabsContent value="community"><CommunityNeeds /></TabsContent>
        <TabsContent value="mine"><MyNeeds /></TabsContent>
        <TabsContent value="assistance"><AssistanceHub /></TabsContent>
      </Tabs>

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto"
          data-testid="otter-assist-preferences-dialog">
          <DialogHeader>
            <DialogTitle>Otter Assist preferences</DialogTitle>
            <DialogDescription>
              The same settings live in Account Settings — they always stay in sync.
            </DialogDescription>
          </DialogHeader>
          <OtterAssistPreferences />
        </DialogContent>
      </Dialog>

      {intent && (
        <OtterAssistEmailIntent deliveryId={intent.deliveryId} intent={intent.intent}
          onClose={() => setIntent(null)} />
      )}
    </div>
  );
}
